import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Tree, NodeRendererProps, NodeApi, TreeApi, MoveHandler } from 'react-arborist';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { DropdownItem } from 'react-bootstrap';
import { ITreeNode } from '../../../models/Interfaces';
import { EventBus } from '../../../helpers/EventBus';
import { IdObj } from 'react-arborist/dist/module/types/utils';
import { TreeItemSort } from '../../../config/Enums';
import { TreeHelper } from '../../../helpers/TreeHelper';


interface ITreeViewOptions {
  buttonclass?: string;
  search?: boolean;
  showselectcaption?: boolean;
  imagesonly?: boolean;
}

export interface ITreeContextMenuItem {
  ItemText: string;
  callbackFunction?: (currentNode: ITreeNode) => void;
  ItemIcon: ReactNode;
}

export interface INodeTypeDefinition {
  parentPropertyName: string;
  displayPropertyName: string;
  typeIconDefinition: IconDefinition;
  nodeTypeValue?: string; // Specific type value this definition represents
  onItemSelect?: (value: any) => void; // Optional custom onItemSelect function for each node type
  contextMenu?: Array<ITreeContextMenuItem>;
  allowDrop?: boolean;
  onItemDrop?: (item: any, target: any) => void;
}

interface ITreeViewProps {
  items: any[];
  idPropertyName: string;
  nodeTypeDefinitions: INodeTypeDefinition[];
  rootNodeKeyValue: string | null;
  closeOnSelect: boolean;
  value?: string | null;
  options?: ITreeViewOptions;
  style?: React.CSSProperties;
  typePropertyName?: string;
  contextMenuItems?: Array<ITreeContextMenuItem>;
  onNodeMove?: (moveditemdata: any, targetitemdata: any | null) => void;
  sortBy: TreeItemSort
}

const GenericTreeView: React.FC<ITreeViewProps> = ({
  items,
  typePropertyName,
  idPropertyName,
  nodeTypeDefinitions,
  rootNodeKeyValue,
  value,
  options = {},
  contextMenuItems,
  onNodeMove,
  sortBy
}) => {
  const [selectedNodeData, setSelectedNodeData] = useState<any | null>(value ?? null);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; nodeId: string | null }>({ x: 0, y: 0, nodeId: null });
  const [treeData, setTreeData] = useState<Array<ITreeNode>>([]);
  const treeRef = useRef<TreeApi<ITreeNode>>(null);
  const ctxMenuRef = useRef<HTMLDivElement>(null);
  const treeBodyRef = useRef<HTMLDivElement>(null);

  const defaultOptions: ITreeViewOptions = {
    buttonclass: 'btn btn-outline-primary',
    search: true,
    showselectcaption: true,
    imagesonly: false,
    ...options,
  };

  const getNodeTypeDef = (nodeType: string | null): INodeTypeDefinition | undefined => {
    if (nodeType !== null) {
      return nodeTypeDefinitions.find(def => def.nodeTypeValue === nodeType);
    } else {
      return nodeTypeDefinitions[0];
    }
  };

  const convertToArboristNode = (item: any): ITreeNode => {
    const coldef = typePropertyName
      ? getNodeTypeDef(item[typePropertyName])
      : getNodeTypeDef(null);

    return {
      id: item[idPropertyName].toString(),
      parentId: item[coldef?.parentPropertyName ?? "parentid"]?.toString() ?? null,
      name: item[coldef?.displayPropertyName ?? "name"],
      selected: false,
      data: item,
      type: typePropertyName ? item[typePropertyName] : "item",
      children: [],
    };
  };

  const convertToHierarchy = (flatData: ITreeNode[]): ITreeNode[] => {
    const buildTree = (parentId: string | null): ITreeNode[] => {
      // Get children
      let children = flatData.filter(item => item.parentId === parentId);

      // Apply sorting based on sortBy value using data[displayKey]
      if (sortBy !== TreeItemSort.Unsorted) {
        children.sort((a: any, b: any) => {
          
          const aTypeDef = typePropertyName ? getNodeTypeDef(a.type) : getNodeTypeDef(null);
          const displayProp = aTypeDef?.displayPropertyName ?? "name";

          const aValue = a.data[displayProp];
          const bValue = b.data[displayProp];
          
          // Handle different data types
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortBy === TreeItemSort.Ascending
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          }
          
          // Handle numbers or fallback comparison
          return sortBy === TreeItemSort.Ascending
            ? (aValue < bValue ? -1 : aValue > bValue ? 1 : 0)
            : (bValue < aValue ? -1 : bValue > aValue ? 1 : 0);
        });
      }

      // Build tree with type checking if typePropertyName exists
      if (typePropertyName) {
        return children.map(item => ({
          ...item,
          children: item.type && item.type === getNodeTypeDef(item.type)?.nodeTypeValue
            ? buildTree(item.id)
            : []
        }));
      } else {
        return children.map(item => ({
          ...item,
          children: buildTree(item.id)
        }));
      }
    };

    return buildTree(rootNodeKeyValue);
  };

  const createContextMenu = () => {
    if (contextMenuItems && contextMenuItems.length > 0) {
      return (
        <div
          className="dropdown-menu"
          ref={ctxMenuRef}
          style={{
            display: 'flex',
            position: 'absolute',
            top: `${ctxMenu.y}px`,
            left: `${ctxMenu.x}px`,
            zIndex: 1080,
          }}
          onMouseLeave={handleCloseMenu}
        >
          <div className='vstack'>
            {contextMenuItems.map((item, index) =>
              <DropdownItem
                onClick={() => item.callbackFunction && ctxMenu.nodeId
                  ? item.callbackFunction(getTreeNodeBoundItem(ctxMenu.nodeId))
                  : null}
                key={`DDI-${index}`}>

                {item.ItemIcon && (
                  item.ItemIcon
                )}

                <span>&nbsp;{item.ItemText}</span>
              </DropdownItem>
            )}
          </div>
        </div>
      );
    } else {
      return null;
    }
  };

  const handlePopupMenu = (event: React.MouseEvent, node: ITreeNode) => {
    event.preventDefault();
    event.stopPropagation();
    const nodebounditem: any = node.data ?? null;
    const treeBody = treeBodyRef.current;

    if (nodebounditem && treeBody) {
      const boundNodeId = nodebounditem[idPropertyName as keyof ITreeNode] as string;
      const rect = treeBody.getBoundingClientRect();
      const menuWidth = 220;
      const menuHeight = (contextMenuItems?.length ?? 0) * 38 + 12;
      const maxX = treeBody.clientWidth - menuWidth - 8;
      const maxY = treeBody.clientHeight + treeBody.scrollTop - menuHeight - 8;
      const x = Math.max(8, Math.min(event.clientX - rect.left + treeBody.scrollLeft, maxX));
      const y = Math.max(8, Math.min(event.clientY - rect.top + treeBody.scrollTop, maxY));

      if (treeRef.current) {
        const nodeApi = treeRef.current.get(boundNodeId.toString());
        if (nodeApi && !nodeApi.isSelected) {
          handleSelectNode(nodeApi);
        }
      }

      setCtxMenu({ x, y, nodeId: boundNodeId });
      ctxMenuRef.current?.classList.add('show');
    }
  };

  const handleCloseMenu = (event: React.MouseEvent) => {
    const menuElement = ctxMenuRef.current;
    if (menuElement && event.relatedTarget instanceof Node) {
      if (!menuElement.contains(event.relatedTarget)) {
        setCtxMenu({ x: 0, y: 0, nodeId: null });
        ctxMenuRef.current?.classList.remove('show');
      }
    } else {
      setCtxMenu({ x: 0, y: 0, nodeId: null });
      ctxMenuRef.current?.classList.remove('show');
    }
  };

  const handleOnNodeMove = (movedata: any): void => {
    if (onNodeMove) {
      const movednode = movedata.dragNodes[0];
      const movedto = movedata.parentNode;

      if (movednode) {
        onNodeMove(movednode.data.data, movedto?.data?.data ?? null);
      }
    }
  };

  const getTreeNodeBoundItem = (nodeId: string): any => {
    let treeNodeBoundItem: any = null;

    if (treeRef.current) {
      const node = treeRef.current.get(nodeId.toString());
      if (node) {
        treeNodeBoundItem = node.data?.data ?? null;
      }
    }
    return treeNodeBoundItem;
  };

  const selectNodeById = (nodeid: string): void => {

    if (treeRef.current) {
      treeRef.current.select({ id: nodeid });

      const node = treeRef.current.get(nodeid);
      if (node) {
        handleSelectNode(node);
      }
    }
  };

  const handleSelectNode = (node: NodeApi<any>) => {
    const nodebounditem: any = node?.data?.data ?? null;
    setSelectedNodeData(nodebounditem);

    if (typePropertyName) {
      const typeDef = getNodeTypeDef(node.data[typePropertyName]);

      // Use onItemSelect only if defined for the specific node type
      if (typeDef && typeDef.onItemSelect) {
        typeDef.onItemSelect(nodebounditem);
      }
    } else {

      const typedef = getNodeTypeDef(null);
      if (typedef && typedef.onItemSelect) {
        typedef.onItemSelect(nodebounditem);
      }
    }
  };

  const nodeRender = (node: NodeRendererProps<any>) => {
    const typeDef = typePropertyName
      ? getNodeTypeDef(node.node.data[typePropertyName])
      : getNodeTypeDef(null);

    if (!typeDef) return null;

    const nodeselected = node.node.isSelected;
    const cls = nodeselected ? "bg-info text-white fw-bold" : "tree-node";
    const haschildren = (node.node.children?.length ?? 0) > 0;

    const handleDragOver = (event: React.DragEvent): void => {
      event.preventDefault();
      if (event.currentTarget) {
        event.currentTarget.classList.add("dragged-over");
      }
    };

    const handleDragLeave = (event: React.DragEvent): void => {
      event.preventDefault();
      if (event.currentTarget) {
        event.currentTarget.classList.remove("dragged-over");
      }
    };

    const handleDrop = (event: React.DragEvent): void => {
      event.preventDefault();

      if (typeDef?.onItemDrop) {
        const source = event.dataTransfer.getData("SOURCE");

        if (source !== "tree-node") {
          if (typeDef.allowDrop) {
            event.currentTarget.classList.remove("dragged-over");
            const data = event.dataTransfer.getData("DRAGGEDOBJECT");
            const droppedItem = JSON.parse(data);
            typeDef.onItemDrop(droppedItem, node.node.data.data);
          }
        }
      }
    };

    const handleNodeDragStart = (event: React.DragEvent): void => {
      event.dataTransfer.setData("SOURCE", "tree-node"); // Mark as an internal tree node
      event.dataTransfer.effectAllowed = "move";
    };

    const dragHandlers = typeDef.allowDrop ? {
      onDragOver: handleDragOver,
      onDrop: handleDrop,
      onDragLeave: handleDragLeave
    } : {};

    const getTypeIcon = () => {
      return <FontAwesomeIcon icon={typeDef.typeIconDefinition} className="text-primary me-1" />;
    };

    return (
      <div
        style={node.style}
        ref={node.dragHandle}
        onDragStart={handleNodeDragStart}
        className={`app-tree-row ${cls} hstack`}
        onClick={() => handleSelectNode(node.node)}
        onContextMenu={(event) => handlePopupMenu(event, node.node.data)}
        {...dragHandlers}
      >
        <div className="m-0 p-0 text-center">
          {!haschildren && (
            <div className="hstack">
              <FontAwesomeIcon icon={faPlus} className='me-1' style={{ visibility: "hidden" }} />
              {getTypeIcon()}
            </div>
          )}
          {haschildren && node.node.isClosed && (
            <div className="hstack" onClick={(e) => { e.stopPropagation(); node.node.toggle(); }}>
              <FontAwesomeIcon icon={faPlus} className="text-primary me-1" style={{ cursor: "pointer" }} />
              {getTypeIcon()}
            </div>
          )}
          {haschildren && node.node.isOpen && (
            <div className="hstack" onClick={(e) => { e.stopPropagation(); node.node.toggle(); }}>
              <FontAwesomeIcon icon={faMinus} className="text-primary me-1" style={{ cursor: "pointer" }} />
              {getTypeIcon()}
            </div>
          )}
        </div>
        <div className="m-0 p-0 w-100" data-nodeid={`node-${node.node.id}`}>
          <span style={{ whiteSpace: "nowrap" }} title={node.node.data.name}>{TreeHelper.getDisplayName(node.node.data.name)}</span>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const adata = items.map(item => {
      return convertToArboristNode(item);
    });

    const hdata = convertToHierarchy(adata);
    setTreeData(hdata);
  }, [items]);

  useEffect(() => {
    EventBus.getInstance().subscribe(EventBus.Event.OnTreeNodeSelect, selectNodeById);
    return () => {
      EventBus.getInstance().unsubscribe(EventBus.Event.OnTreeNodeSelect, selectNodeById);
    }
  }, []);

  return (
    <div ref={treeBodyRef} style={{ height: "100%", width: "100%", position: "relative", overflow: "auto" }} className="border ps-2">
      {treeData.length > 0 && (
        <>
          <Tree
            data={treeData}
            openByDefault={false}
            disableMultiSelection={true}
            width={"100%"}
            ref={treeRef}
            onMove={handleOnNodeMove}
          >
            {nodeRender}
          </Tree>
          {contextMenuItems && (
            ctxMenu.nodeId && createContextMenu()
          )}
        </>
      )}
    </div>
  );
};

export default GenericTreeView;
